import React, { useState, useEffect, useMemo } from 'react';
import { getUsers, resetUserPassword, approveUser, rejectUser, deactivateUser, deleteUser } from '../lib/api';
import { formatDateTime, formatApiErrorDetail } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Users as UsersIcon, Key, AlertCircle, Copy, Check, UserPlus, UserCheck, UserX, Ban, Trash2 } from 'lucide-react';
import { FormShortcutHints } from '../components/ui/form-shortcut-hints';
import { toast } from 'sonner';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const [expandedUserIds, setExpandedUserIds] = useState([]);
  const toggleExpandUser = (id) => setExpandedUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Pre-partition users to avoid re-filtering on every render
  const pendingUsers = useMemo(() => users.filter(u => u.status === 'pending'), [users]);
  const activeUsers = useMemo(() => users.filter(u => u.status !== 'pending'), [users]);

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setResetting(true);
    try {
      const res = await resetUserPassword(selectedUser.id);
      setNewPassword(res.data.new_password);
      toast.success('Password reset successfully');
      fetchUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setResetDialogOpen(false);
    } finally {
      setResetting(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await approveUser(userId);
      toast.success('User approved');
      fetchUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Reject and remove this user?')) return;
    try {
      await rejectUser(userId);
      toast.success('User rejected');
      fetchUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDeactivate = async (userId) => {
    try {
      await deactivateUser(userId);
      toast.success('User status updated');
      fetchUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Permanently delete this user? This cannot be undone.')) return;
    try {
      await deleteUser(userId);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setCopied(false);
    setResetDialogOpen(true);
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(newPassword);
    setCopied(true);
    toast.success('Password copied to clipboard');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-3xl font-bold tracking-tight">User Management</h1>
        <p className="text-sm text-muted-foreground hidden sm:block">Manage users, approve registrations, and reset passwords</p>
      </div>

      {/* Pending Users */}
      {pendingUsers.length > 0 && (
        <Card className="border-amber-300 dark:border-amber-700">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <UserPlus className="h-5 w-5" />
              Pending Approvals ({pendingUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingUsers.map(user => (
                    <TableRow key={user.id} data-testid={`pending-user-${user.id}`}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>@{user.username}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDateTime(user.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" onClick={() => handleApprove(user.id)} data-testid={`approve-user-${user.id}`}>
                            <UserCheck className="h-4 w-4 mr-1" />Approve
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => handleReject(user.id)} data-testid={`reject-user-${user.id}`}>
                            <UserX className="h-4 w-4 mr-1" />Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <UsersIcon className="h-5 w-5" />
            Users ({activeUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden sm:table-cell">Username</TableHead>
                  <TableHead className="hidden sm:table-cell">Role</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right hidden sm:table-cell">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeUsers.map(user => {
                  const isExpanded = expandedUserIds.includes(user.id);
                  return (
                  <React.Fragment key={user.id}>
                  <TableRow data-testid={`user-row-${user.id}`} className="sm:cursor-default cursor-pointer" onClick={() => toggleExpandUser(user.id)}>
                    <TableCell className="font-medium text-sm">
                      <span className="sm:hidden mr-1 text-muted-foreground">{isExpanded ? '▾' : '▸'}</span>
                      {user.name}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">@{user.username}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {user.status === 'deactivated' ? (
                        <Badge variant="destructive" className="text-xs">Deactivated</Badge>
                      ) : user.must_change_password ? (
                        <Badge variant="warning" className="text-xs">Must Change PW</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right hidden sm:table-cell">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); openResetDialog(user); }}
                          data-testid={`reset-password-${user.id}`}
                        >
                          <Key className="h-3.5 w-3.5 mr-1" />
                          Reset
                        </Button>
                        {user.role !== 'admin' ? (
                          <Button
                            variant={user.status === 'deactivated' ? 'default' : 'secondary'}
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDeactivate(user.id); }}
                            data-testid={`deactivate-user-${user.id}`}
                          >
                            <Ban className="h-3.5 w-3.5 mr-1" />
                            {user.status === 'deactivated' ? 'Activate' : 'Deactivate'}
                          </Button>
                        ) : (
                          <Button variant="secondary" size="sm" className="invisible">
                            <Ban className="h-3.5 w-3.5 mr-1" />Deactivate
                          </Button>
                        )}
                        {user.role !== 'admin' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={(e) => { e.stopPropagation(); handleDeleteUser(user.id); }}
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-8 w-8 invisible">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow className="sm:hidden bg-muted/20">
                      <TableCell colSpan={2} className="py-2 px-4">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
                          <span className="text-muted-foreground">Username</span>
                          <span className="text-right">@{user.username}</span>
                          <span className="text-muted-foreground">Role</span>
                          <span className="text-right capitalize">{user.role}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); openResetDialog(user); }} data-testid={`reset-password-mobile-${user.id}`}>
                            <Key className="h-3 w-3 mr-1" />Reset PW
                          </Button>
                          {user.role !== 'admin' && (
                            <Button variant={user.status === 'deactivated' ? 'default' : 'secondary'} size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleDeactivate(user.id); }}>
                              <Ban className="h-3 w-3 mr-1" />{user.status === 'deactivated' ? 'Activate' : 'Deactivate'}
                            </Button>
                          )}
                          {user.role !== 'admin' && (
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteUser(user.id); }}>
                              <Trash2 className="h-3 w-3 mr-1" />Delete
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          
          {!newPassword ? (
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Are you sure you want to reset the password for <strong>{selectedUser?.name}</strong>?
                  A new temporary password will be generated and the user will be required to change it on next login.
                </AlertDescription>
              </Alert>
              <DialogFooter>
                <FormShortcutHints />
                <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleResetPassword} disabled={resetting} data-testid="confirm-reset-password">
                  {resetting ? 'Resetting...' : 'Reset Password'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert>
                <Check className="h-4 w-4 text-income" />
                <AlertDescription>
                  Password has been reset successfully. Please share the new password with the user.
                </AlertDescription>
              </Alert>
              
              <div className="p-4 rounded-md bg-muted">
                <p className="text-sm text-muted-foreground mb-2">New Password</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-background rounded border font-mono text-sm">
                    {newPassword}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copyPassword}
                    data-testid="copy-password"
                  >
                    {copied ? <Check className="h-4 w-4 text-income" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <DialogFooter>
                <Button onClick={() => setResetDialogOpen(false)}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
